import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Progress, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudUploadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { listBulkBatches, type BulkBatch } from '@/api/bulk';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { UploadDrawer } from '@/bulk/UploadDrawer';
import { BATCH_STATUS_COLOR, BATCH_STATUS_LABEL, isLive, progressPercent, SUBJECT_TYPE_LABEL } from '@/bulk/bulkModel';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';

const { Text } = Typography;

const PAGE_SIZE = 25;
const POLL_MS = 4000;

export function BulkPage() {
  usePageTitle('Bulk');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const flowParam = params.get('flowId') ?? undefined;
  const [items, setItems] = useState<BulkBatch[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(Boolean(flowParam));

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const res = await listBulkBatches(page, PAGE_SIZE);
        setItems(res.items);
        setTotal(res.totalCount);
        setUnavailable(false);
      } catch (e) {
        const err = normalizeApiError(e);
        if (isNotAvailable(err)) setUnavailable(true);
        else setError(err.message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [page],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const anyLive = items.some((b) => isLive(b.status));
  useEffect(() => {
    if (!anyLive) return;
    const timer = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [anyLive, load]);

  const columns: ColumnsType<BulkBatch> = [
    {
      title: 'Batch',
      dataIndex: 'name',
      render: (name: string, b) => (
        <Link to={`/bulk/${encodeURIComponent(b.batchId)}`} data-testid="bulk-batch-link">
          {name}
        </Link>
      ),
    },
    { title: 'Type', dataIndex: 'subjectType', width: 150, render: (t: BulkBatch['subjectType']) => SUBJECT_TYPE_LABEL[t] },
    {
      title: 'Flow',
      dataIndex: 'flowId',
      render: (f: string, b) => (
        <Text className="ol-mono" style={{ fontSize: 12 }}>
          {f} v{b.flowVersion}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 170,
      render: (s: BulkBatch['status']) => (
        <Tag bordered={false} color={BATCH_STATUS_COLOR[s]} data-testid="bulk-batch-status">
          {BATCH_STATUS_LABEL[s]}
        </Tag>
      ),
    },
    { title: 'Rows', dataIndex: 'totalRows', width: 70, align: 'right' },
    {
      title: 'Started',
      width: 90,
      align: 'right',
      render: (_, b) => b.counts.started,
    },
    {
      title: 'Progress',
      width: 130,
      render: (_, b) => <Progress percent={progressPercent(b)} size="small" />,
    },
    { title: 'By', dataIndex: 'createdBy', ellipsis: true },
    { title: 'Uploaded', dataIndex: 'createdAt', width: 170, render: (v: string) => new Date(v).toLocaleString() },
  ];

  const openDrawer = () => setDrawer(true);

  return (
    <div className="ol-page">
      <PageHeader
        title="Bulk"
        icon={<CloudUploadOutlined />}
        subtitle="Start many runs of a published flow from a CSV. Every row is checked first, and nothing starts until you confirm."
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              Refresh
            </Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={openDrawer} data-testid="bulk-upload-open">
              Upload a CSV
            </Button>
          </Space>
        }
      />
      {unavailable ? (
        <EmptyState title="Bulk is not available here" description="This deployment does not serve the bulk API." />
      ) : error ? (
        <ErrorState what="Could not load batches" detail={error} onRetry={() => void load()} />
      ) : loading ? (
        <TableSkeleton rows={6} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No batches yet"
          description="Upload a CSV against a published flow. You can download a template for individuals, companies or connection groups."
          action={
            <Button type="primary" icon={<UploadOutlined />} onClick={openDrawer}>
              Upload a CSV
            </Button>
          }
        />
      ) : (
        <Table
          rowKey="batchId"
          size="small"
          dataSource={items}
          columns={columns}
          pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, onChange: setPage }}
        />
      )}
      <UploadDrawer
        open={drawer}
        flowId={flowParam}
        onClose={() => {
          setDrawer(false);
          if (flowParam) setParams({});
        }}
        onValidated={(batch) => navigate(`/bulk/${encodeURIComponent(batch.batchId)}`)}
      />
    </div>
  );
}
